import { useCallback, useEffect, useState } from "react";
import { parseVmId, type VmPreference } from "~/lib/auth/vm-types";

const VM_PREFERENCE_STORAGE_KEY = "glider:webapp-v2:vm-preference";
const VM_PREFERENCE_CHANGE_EVENT = "glider:webapp-v2:vm-preference-changed";

function dispatchVmPreferenceChanged(preference: VmPreference): void {
  if (typeof window === "undefined") {
    return;
  }

  const CustomEventConstructor = window.CustomEvent;
  if (typeof CustomEventConstructor !== "function") {
    return;
  }

  window.dispatchEvent(
    new CustomEventConstructor<VmPreference>(VM_PREFERENCE_CHANGE_EVENT, {
      detail: preference,
    }),
  );
}

function normalizeVmPreference(value: unknown): VmPreference {
  if (typeof value === "string" && value.trim().toLowerCase() === "auto") {
    return "auto";
  }

  return parseVmId(value) ?? "auto";
}

export function getVmPreference(): VmPreference {
  if (typeof window === "undefined") {
    return "auto";
  }

  return normalizeVmPreference(localStorage.getItem(VM_PREFERENCE_STORAGE_KEY));
}

export function setVmPreference(preference: VmPreference): void {
  if (typeof window === "undefined") {
    return;
  }

  localStorage.setItem(VM_PREFERENCE_STORAGE_KEY, preference);
  dispatchVmPreferenceChanged(preference);
}

export function clearVmPreference(): void {
  if (typeof window === "undefined") {
    return;
  }

  localStorage.removeItem(VM_PREFERENCE_STORAGE_KEY);
  dispatchVmPreferenceChanged("auto");
}

export function useVmPreference() {
  const [preference, setPreferenceState] = useState<VmPreference>("auto");
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setPreferenceState(getVmPreference());
    setIsLoaded(true);

    const onPreferenceChanged = (event: Event) => {
      const customEvent = event as CustomEvent<VmPreference>;
      setPreferenceState(customEvent.detail);
    };

    window.addEventListener(VM_PREFERENCE_CHANGE_EVENT, onPreferenceChanged);

    return () => {
      window.removeEventListener(
        VM_PREFERENCE_CHANGE_EVENT,
        onPreferenceChanged,
      );
    };
  }, []);

  const setPreference = useCallback((nextPreference: VmPreference) => {
    setVmPreference(nextPreference);
  }, []);

  const resetPreference = useCallback(() => {
    clearVmPreference();
  }, []);

  return {
    preference,
    isLoaded,
    setPreference,
    resetPreference,
  };
}
